import { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowLeft, PaperPlaneTilt, CheckCircle, EnvelopeSimple, ShieldCheck, Sparkle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUBJECTS = [
  { value: 'general', label: 'General question' },
  { value: 'sales', label: 'Sales / pricing' },
  { value: 'support', label: 'I need help (existing customer)' },
  { value: 'bug', label: 'Report a bug' },
  { value: 'privacy', label: 'Privacy / data request' },
  { value: 'affiliates', label: 'Affiliate program' },
  { value: 'security', label: 'Security issue' },
];

/**
 * Public contact form. Submits to /api/contact/submit which emails the
 * routed recipient via Resend. No login required.
 */
export default function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('general');
  const [message, setMessage] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState(''); // honeypot
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim() || message.trim().length < 10) {
      setError('Please fill in name, email, and a message of at least 10 characters.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/contact/submit`, {
        name: name.trim(),
        email: email.trim(),
        subject,
        message: message.trim(),
        company_website: companyWebsite,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again or email chip@migrationcrunch.com directly.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="contact-page">
      {/* Top bar */}
      <header className="border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="back-home-link">
            <div className="w-8 h-8 bg-[#002FA7] rounded flex items-center justify-center">
              <span className="text-white font-black text-sm">SC</span>
            </div>
            <span className="text-lg font-black tracking-tight">SalesCrunch<span className="text-[#F39C12]">CRM</span></span>
          </Link>
          <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
            <ArrowLeft size={14} weight="bold" /> Back home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="inline-flex items-center gap-2 bg-[#F39C12]/10 border border-[#F39C12]/30 rounded-full px-3 py-1 text-xs font-semibold text-[#B8730A] mb-4">
          <EnvelopeSimple size={12} weight="fill" /> CONTACT US
        </div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-4 text-slate-900">
          Real humans.<br/><span className="text-[#002FA7]">Reply in 24 hours.</span>
        </h1>
        <p className="text-lg text-slate-600 mb-10 max-w-2xl leading-relaxed">
          Questions about SalesCrunchCRM? Privacy or affiliate inquiries?
          Send us a note — a real person will read it and reply within one business day.
        </p>

        {done ? (
          <div
            className="bg-white border border-emerald-200 rounded-lg p-8 text-center shadow-sm"
            data-testid="contact-success"
          >
            <CheckCircle size={48} weight="fill" className="text-emerald-500 mx-auto mb-3" />
            <h2 className="text-2xl font-black text-slate-900 mb-2">Got it — thanks {name.split(' ')[0]}!</h2>
            <p className="text-slate-600 mb-6">
              Your message landed in our inbox. We&apos;ll get back to you at <strong>{email}</strong> within one business day.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-[#F39C12] hover:bg-[#E08C00] text-black font-bold px-6 py-2.5 rounded-md text-sm transition-colors"
              data-testid="contact-back-home-cta"
            >
              Back to homepage
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            <form
              onSubmit={submit}
              className="md:col-span-2 bg-white border border-slate-200 rounded-lg p-6 shadow-sm"
              data-testid="contact-form"
            >
              {/* Honeypot — hidden from humans, bots will fill it */}
              <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                <label>
                  Company website (leave blank)
                  <input
                    type="text"
                    tabIndex="-1"
                    autoComplete="off"
                    value={companyWebsite}
                    onChange={(e) => setCompanyWebsite(e.target.value)}
                  />
                </label>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-xs font-bold uppercase tracking-wide text-slate-700 mb-1">
                    Your name *
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Chip Tippmann"
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs font-bold uppercase tracking-wide text-slate-700 mb-1">
                    Email *
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourcompany.com"
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="contact-subject" className="block text-xs font-bold uppercase tracking-wide text-slate-700 mb-1">
                  Topic
                </label>
                <select
                  id="contact-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent bg-white"
                  data-testid="contact-subject-select"
                >
                  {SUBJECTS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label htmlFor="contact-message" className="block text-xs font-bold uppercase tracking-wide text-slate-700 mb-1">
                  Message *
                </label>
                <textarea
                  id="contact-message"
                  required
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us what's on your mind…"
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent resize-y leading-relaxed"
                  data-testid="contact-message-input"
                />
                <p className="text-[11px] text-slate-400 mt-1">{message.length}/5000 characters</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-3 py-2 mb-4" data-testid="contact-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="bg-[#F39C12] hover:bg-[#E08C00] disabled:opacity-60 disabled:cursor-not-allowed text-black font-bold px-6 py-3 rounded-md inline-flex items-center gap-2 transition-colors"
                data-testid="contact-submit"
              >
                {busy ? 'Sending…' : <>Send message <PaperPlaneTilt size={16} weight="fill" /></>}
              </button>
              <p className="text-[11px] text-slate-400 mt-3">
                We use your email only to reply. No marketing list. See our{' '}
                <a
                  href="https://app.termly.io/policy-viewer/policy.html?policyUUID=31b34c66-4334-40a3-b0ad-bfb742f1940f"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#002FA7] hover:underline"
                >
                  Privacy Policy
                </a>.
              </p>
            </form>

            {/* Side info card */}
            <aside className="space-y-4">
              <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkle size={16} weight="fill" className="text-[#F39C12]" />
                  <h3 className="font-bold text-sm text-slate-900">Already a customer?</h3>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed mb-2">
                  Ask <strong>Crunchy</strong> &mdash; your built-in AI concierge &mdash; right inside the app.
                  He answers most questions instantly.
                </p>
                <Link
                  to="/login"
                  className="text-xs font-bold text-[#002FA7] hover:underline inline-flex items-center gap-1"
                  data-testid="contact-login-link"
                >
                  Sign in →
                </Link>
              </div>

              <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck size={16} weight="fill" className="text-emerald-500" />
                  <h3 className="font-bold text-sm text-slate-900">Direct email addresses</h3>
                </div>
                <ul className="text-xs text-slate-600 space-y-1.5">
                  <li>
                    <strong className="text-slate-700">Support:</strong>{' '}
                    <a className="text-[#002FA7] hover:underline" href="mailto:chip@migrationcrunch.com">chip@migrationcrunch.com</a>
                  </li>
                  <li>
                    <strong className="text-slate-700">Privacy:</strong>{' '}
                    <a className="text-[#002FA7] hover:underline" href="mailto:privacy@migrationcrunch.com">privacy@migrationcrunch.com</a>
                  </li>
                  <li>
                    <strong className="text-slate-700">Affiliates:</strong>{' '}
                    <a className="text-[#002FA7] hover:underline" href="mailto:affiliates@migrationcrunch.com">affiliates@migrationcrunch.com</a>
                  </li>
                  <li>
                    <strong className="text-slate-700">Security:</strong>{' '}
                    <a className="text-[#002FA7] hover:underline" href="mailto:security@migrationcrunch.com">security@migrationcrunch.com</a>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        )}
      </main>

      <footer className="border-t border-slate-200 bg-slate-50 mt-12 py-6 text-center text-xs text-slate-500">
        © 2026 SalesCrunchCRM. Made for the rest of us.
      </footer>
    </div>
  );
}
